import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Modal from '../../components/ui/Modal';
import Input, { Textarea } from '../../components/ui/Input';
import Pagination from '../../components/ui/Pagination';
import { Icon } from '../../components/ui/Icon';
import ActionIconButton from '../../components/ui/ActionIconButton';
import DeleteModal from '../../components/ui/DeleteModal';
import ColumnValuePicker from '../../components/ui/ColumnValuePicker';

import {
  fetchParameters,
  createParameter,
  updateParameter,
  deleteParameter,
} from '../../store/slices/parametersSlice';
import { fetchChTables, fetchChColumns } from '../../store/slices/metadataSlice';
import { pushToast } from '../../store/slices/uiSlice';
import { PARAM_TYPES } from '../../constants/navigation';
import { parameterSchema, validate, firstError } from '../../utils/validation';

// Refactor 2026-04 (backend migration 0006):
// Bu ekran artik "Sistem Sabitleri". scope/game_period_id kaldirildi —
// donem-ozel is kurallari Donemler ekraninda, gorev-ozel filtreler
// Gorevler ekraninda tutulur. Burada sadece tum gorevlerde ortak
// kullanilan teknik listeler yer alir (AKTIF_POLICE_DURUMLARI,
// HYO_HARIC_BRANSLAR, BRANS_KODLARI vb.).

function ParamValueDisplay({ param }) {
  const val = param.param_value;
  if (param.param_type === 'LIST' && Array.isArray(val)) {
    return (
      <div className="flex flex-wrap gap-1">
        {val.slice(0, 3).map((v, i) => (
          <Badge key={i} color="blue" size="sm">
            {v}
          </Badge>
        ))}
        {val.length > 3 && <span className="text-xs text-neutral-400">+{val.length - 3}</span>}
      </div>
    );
  }
  if (param.param_type === 'BOOLEAN') {
    return <Badge color={val ? 'green' : 'neutral'}>{val ? 'Evet' : 'Hayır'}</Badge>;
  }
  return <span className="text-sm font-medium">{String(val)}</span>;
}

function ParamForm({ open, onClose, onSaved, initial }) {
  const dispatch = useDispatch();

  // ── Metadata (tablolar + kolonlar) ────────────────────────────────────────
  const { tables, columns: columnsMap } = useSelector((s) => s.metadata);

  const [form, setForm] = useState({
    param_key: '',
    param_value: '',
    param_type: 'LIST',
    label: '',
    description: '',
  });
  // FILTRE modunda seçilen tablo/kolon
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedColumn, setSelectedColumn] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  // Ref'ler: edit modda pre-fill sırasında yan etkileri (kolon/form sıfırlama) engeller
  const skipTableEffect = useRef(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Tablo+kolon picker ve değer picker: LIST tipinde aktif
  const showTablePicker = form.param_type === 'LIST';
  const columns = columnsMap[selectedTable] || [];

  // Modal açıldığında tabloları yükle
  useEffect(() => {
    if (open && tables.length === 0) dispatch(fetchChTables());
  }, [open, dispatch, tables.length]);

  // initial veya open değişince formu sıfırla / pre-fill et
  useEffect(() => {
    if (initial) {
      setForm({
        param_key: initial.param_key || '',
        param_value: initial.param_value ?? '',
        param_type: initial.param_type || 'LIST',
        label: initial.label || '',
        description: initial.description || '',
      });
      const preTable = initial.source_table || '';
      const preCol   = initial.source_column || '';
      if (preTable) {
        // Tablo effect'in selectedColumn + form'u sıfırlamasını engelle
        skipTableEffect.current = true;
        setSelectedTable(preTable);
        setSelectedColumn(preCol);
        // Kolonlar henüz yüklü değilse çek
        if (!columnsMap[preTable]) dispatch(fetchChColumns(preTable));
      } else {
        setSelectedTable('');
        setSelectedColumn('');
      }
    } else {
      setForm({ param_key: '', param_value: '', param_type: 'LIST', label: '', description: '' });
      setSelectedTable('');
      setSelectedColumn('');
    }
    setErrors({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, open]);

  // Tablo seçilince kolonları yükle (kullanıcı etkileşimi)
  // skipTableEffect: edit modda pre-fill sırasında kolon/form sıfırlamasını engeller
  useEffect(() => {
    if (skipTableEffect.current) {
      skipTableEffect.current = false;
      return;
    }
    if (selectedTable) dispatch(fetchChColumns(selectedTable));
    setSelectedColumn('');
    // LIST tipinde key+label sıfırlanır (kolon seçince yeniden dolar).
    // NUMBER/STRING/FORMULA: kullanıcının yazdığı key/label korunur —
    // tablo+kolon sadece kaynak belgeleme amaçlı, auto-fill istemiyoruz.
    if (form.param_type === 'LIST') {
      setForm((f) => ({ ...f, param_key: '', label: '' }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTable, dispatch]);

  // Kolon seçilince key + label otomatik doldur — YALNIZCA LIST tipinde.
  // NUMBER/STRING/FORMULA: kullanıcının girdiği key/label üzerine yazma.
  // `columns` deps'e dahil edilmedi: kolonlar yüklenince edit modda
  // mevcut param_key/label üzerine yazılmasın.
  useEffect(() => {
    if (!selectedColumn || !columns.length) return;
    if (form.param_type !== 'LIST') return;  // LIST dışında auto-fill yok
    const col = columns.find((c) => c.key === selectedColumn);
    if (!col) return;
    setForm((f) => ({
      ...f,
      param_key: selectedColumn.toUpperCase(),
      label: col.label || selectedColumn,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumn]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const handleSave = async () => {
    const { ok: valid, errors: errs } = validate(parameterSchema, form);
    if (!valid) {
      setErrors(errs);
      dispatch(pushToast({ kind: 'error', message: firstError(errs) || 'Form eksik' }));
      return;
    }
    setErrors({});
    setSaving(true);

    let valueToSend = form.param_value;
    if (form.param_type === 'LIST') {
      if (Array.isArray(valueToSend)) {
        // Zaten dizi (picker'dan geldi)
      } else if (typeof valueToSend === 'string') {
        valueToSend = valueToSend.split(',').map((v) => v.trim()).filter(Boolean);
      }
    }
    if (form.param_type === 'NUMBER') valueToSend = Number(valueToSend);
    if (form.param_type === 'BOOLEAN') {
      valueToSend = form.param_value === true || form.param_value === 'true';
    }

    const payload = {
      param_key: form.param_key,
      label: form.label,
      param_type: form.param_type,
      param_value: valueToSend,
      category: 'FILTRE',
      description: form.description,
      source_table: selectedTable || null,
      source_column: selectedColumn || null,
    };

    const action = initial
      ? updateParameter({ id: initial.id, payload })
      : createParameter(payload);
    const res = await dispatch(action);
    setSaving(false);
    if (res.meta.requestStatus === 'fulfilled') {
      dispatch(pushToast({
        kind: 'success',
        message: initial ? 'Sistem sabiti güncellendi' : 'Sistem sabiti oluşturuldu',
      }));
      onSaved?.();
      onClose();
    } else {
      // Unique constraint → key çakışması
      const errMsg = res.payload || '';
      const isDuplicate =
        errMsg.toLowerCase().includes('unique') ||
        errMsg.toLowerCase().includes('duplicate') ||
        errMsg.toLowerCase().includes('already exists') ||
        errMsg.toLowerCase().includes('conflict');
      dispatch(pushToast({
        kind: 'error',
        message: isDuplicate
          ? `"${form.param_key}" sabit adı zaten kullanılıyor — farklı bir key girin`
          : errMsg || 'Kaydedilemedi',
      }));
    }
  };

  // Seçili değerleri virgüllü string veya array olarak normalize et
  const currentValues = Array.isArray(form.param_value)
    ? form.param_value
    : typeof form.param_value === 'string' && form.param_value
      ? form.param_value.split(',').map((v) => v.trim()).filter(Boolean)
      : [];

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={initial ? 'Sistem Sabiti Düzenle' : 'Yeni Sistem Sabiti'}
        size="md"
        closeOnBackdrop={false}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>İptal</Button>
            <Button loading={saving} onClick={handleSave}>
              {initial ? 'Güncelle' : 'Oluştur'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">

          {/* ── Tip ── */}
          <Select
            label="Tip"
            value={form.param_type}
            onChange={(e) => set('param_type', e.target.value)}
            options={PARAM_TYPES.map((p) => ({ value: p.value, label: p.label }))}
          />

          {/* ── LIST tipinde tablo → kolon seçimi (FILTRE ve REFERANS her ikisi için) ── */}
          {showTablePicker && (
            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Tablo"
                value={selectedTable}
                onChange={(e) => setSelectedTable(e.target.value)}
                placeholder="Tablo seç…"
                options={tables.map((t) => ({ value: t.key, label: t.label }))}
              />
              <Select
                label="Kolon (Sabit Adı)"
                value={selectedColumn}
                onChange={(e) => setSelectedColumn(e.target.value)}
                placeholder={selectedTable ? 'Kolon seç…' : 'Önce tablo seç'}
                options={columns.map((c) => ({ value: c.key, label: c.label || c.key }))}
                disabled={!selectedTable}
              />
            </div>
          )}

          {/* ── Sabit Adı (key) + Görünen Ad ── */}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Sabit Adı (key)"
              required
              value={form.param_key}
              onChange={(e) => set('param_key', e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
              placeholder="AKTIF_POLICE_DURUMLARI"
              hint="Görevlerde ${KEY} olarak referans verilir"
              disabled={initial?.is_system}
              error={errors.param_key}
            />
            <Input
              label="Görünen Ad"
              required
              value={form.label}
              onChange={(e) => set('label', e.target.value)}
              placeholder="Aktif Poliçe Durumları"
              error={errors.label}
            />
          </div>

          {/* ── Değer alanı ── */}
          {form.param_type === 'BOOLEAN' ? (
            <Select
              label="Değer"
              value={String(form.param_value)}
              onChange={(e) => set('param_value', e.target.value === 'true')}
              options={[{ value: 'true', label: 'Evet' }, { value: 'false', label: 'Hayır' }]}
              error={errors.param_value}
            />
          ) : form.param_type === 'NUMBER' ? (
            <Input
              label="Değer"
              type="number"
              value={form.param_value}
              onChange={(e) => set('param_value', e.target.value)}
              error={errors.param_value}
            />
          ) : form.param_type === 'LIST' ? (
            /* LIST → chip gösterimi her zaman aktif (FILTRE ve REFERANS), picker için kolon seçimi gerekli */
            <div>
              <p className="text-xs font-medium text-neutral-700 mb-1">
                Değerler <span className="text-red-500">*</span>
              </p>
              <div className="rounded-lg border border-neutral-200 p-2 min-h-[2.5rem] flex flex-wrap gap-1 items-center">
                {currentValues.length === 0 && (
                  <span className="text-xs text-neutral-400">
                    {selectedColumn ? 'Henüz değer seçilmedi' : 'Tablo ve kolon seçerek değer ekleyin'}
                  </span>
                )}
                {currentValues.map((v) => (
                  <Badge key={v} color="blue" size="sm">
                    {v}
                    <button
                      type="button"
                      onClick={() => set('param_value', currentValues.filter((x) => x !== v))}
                      className="ml-1 text-blue-400 hover:text-blue-700"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
              {selectedColumn ? (
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="mt-1.5 flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800"
                >
                  <Icon name="plus" className="h-3.5 w-3.5" />
                  Değerden Seç
                </button>
              ) : (
                <p className="mt-1.5 text-xs text-neutral-400 italic">
                  Picker ile değer eklemek için yukarıdan tablo ve kolon seçin.
                </p>
              )}
              {errors.param_value && (
                <p className="text-xs text-red-500 mt-1">{errors.param_value}</p>
              )}
            </div>
          ) : (
            <Input
              label="Değer"
              value={form.param_value}
              onChange={(e) => set('param_value', e.target.value)}
              error={errors.param_value}
            />
          )}

          <Textarea
            label="Açıklama (opsiyonel)"
            hint="Bu sabit hangi görevlerde, nasıl kullanılır?"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            rows={2}
          />
        </div>
      </Modal>

      {/* ColumnValuePicker — FILTRE + LIST + kolon seçiliyken */}
      {selectedTable && selectedColumn && (
        <ColumnValuePicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          table={selectedTable}
          column={selectedColumn}
          op="IN"
          current={currentValues}
          onPick={(val) => {
            // val: virgüllü string → array'e çevir
            const arr = typeof val === 'string'
              ? val.split(',').map((v) => v.trim()).filter(Boolean)
              : val;
            set('param_value', arr);
          }}
        />
      )}
    </>
  );
}

function SistemSabitleriInfoBox() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#E1E1E1]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-sky-50/60 hover:bg-sky-100/50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <Icon name="settings" className="h-4 w-4 text-sky-600 flex-shrink-0" />
          <span className="text-sm font-medium text-sky-800">Sistem Sabitleri nedir, nerede kullanılır?</span>
        </div>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} className="h-4 w-4 text-sky-500 flex-shrink-0" />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-2 space-y-2 text-sm text-sky-950 bg-sky-50/40">
          <p>
            Birden fazla görevde tekrar eden sabit değerler burada tanımlanır.
            Görev filtrelerinde <code className="bg-sky-100 px-1 rounded text-xs font-mono">${'{SABIT_ADI}'}</code> yazarak
            referans verilir — sabiti bir yerden değiştirince tüm görevlere yansır.
          </p>
          <div className="rounded-lg bg-sky-100/60 border border-sky-200 px-3 py-2 space-y-1">
            <p className="text-xs text-sky-900/80">Örnek sabitler:</p>
            <ul className="text-xs text-sky-800 space-y-0.5">
              <li><code className="font-mono">AKTIF_POLICE_DURUMLARI</code> — aktif sayılan poliçe durumları</li>
              <li><code className="font-mono">HYO_HARIC_BRANSLAR</code> — HYO dışı tutulan branşlar</li>
              <li><code className="font-mono">YENI_YENILEME_DEGERLERI</code> — dahil edilen poliçe tipleri</li>
              <li><code className="font-mono">FAAL_ACENTE_DURUMU</code> — faal acente durumu kodu</li>
              <li><code className="font-mono">BRANS_KODLARI</code> — tüm branş kodları listesi</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Parameters() {
  const dispatch = useDispatch();
  const { items, status, pagination } = useSelector((s) => s.parameters);
  const [showForm, setShowForm] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Kullanim sayisi > 0 olan parametreler icin silme yerine uyari modal'i.
  // Backend silmeyi tetiklersek aktif gorevlerin filter referanslari kirilir
  // (${PARAM_KEY} dangling kalir → evaluation hatasi). UI seviyesinde engelle.
  const [blockedDeleteTarget, setBlockedDeleteTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);

  useEffect(() => {
    dispatch(fetchParameters({ page, size }));
  }, [dispatch, page, size]);

  const columns = [
    {
      key: 'key',
      header: 'Sabit',
      render: (r) => (
        <div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-mono text-xs font-semibold text-primary-700">{r.param_key}</p>
            {r.is_system && (
              <Badge color="purple" size="sm">
                <Icon name="settings" className="h-3 w-3 mr-0.5" />
                Sistem
              </Badge>
            )}
          </div>
          <p className="text-xs text-neutral-600 mt-0.5">{r.label}</p>
          {r.description && (
            <p className="text-xs text-neutral-400 mt-0.5 leading-relaxed max-w-xs truncate" title={r.description}>
              {r.description}
            </p>
          )}
          {(r.source_table || r.source_column) && (
            <p className="text-xs text-neutral-400 mt-0.5 font-mono">
              {r.source_table}
              {r.source_column && (
                <span className="text-neutral-300 mx-0.5">›</span>
              )}
              {r.source_column}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Tip',
      render: (r) => {
        const pt = PARAM_TYPES.find((x) => x.value === r.param_type);
        return <Badge color={pt?.color || 'neutral'}>{r.param_type}</Badge>;
      },
    },
    {
      key: 'value',
      header: 'Değer',
      render: (r) => <ParamValueDisplay param={r} />,
    },
    {
      key: 'usage',
      header: 'Kullanım',
      render: (r) => (
        <span className="text-xs text-neutral-600">{r.usage_count || 0}× kullanılıyor</span>
      ),
    },
    {
      key: 'actions',
      header: 'İşlemler',
      className: 'text-right',
      headerClassName: 'text-right',
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <ActionIconButton
            icon="edit"
            iconColor="#3CBFFF"
            title="Düzenle"
            onClick={() => setShowForm({ initial: r })}
          />
          {/* Silme butonu her zaman gosterilir. Silme karari is_system'e degil,
              SADECE usage_count'a bagli:
                - usage_count > 0 → uyari modal'i acilir, silinmez
                - usage_count = 0 → onay modal'i acilir, "Sil" tiklaninca silinir
              is_system flag'i artik silme kisitlamasi degil. */}
          <ActionIconButton
            icon="trash"
            iconColor="#FD5995"
            title={Number(r.usage_count || 0) > 0
              ? `${r.usage_count} görevde kullanılıyor — silmek için önce kullanan görevleri güncelleyin`
              : "Sil"}
            onClick={() => {
              if (Number(r.usage_count || 0) > 0) {
                setBlockedDeleteTarget(r);
              } else {
                setDeleteTarget(r);
              }
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Sistem Sabitleri"
        subtitle="Tüm görevlerde ortak kullanılan teknik listeler (filtre değerleri, referans kodları)"
        className="bg-white border border-[#E1E1E1] shadow-card rounded-[1.25rem] px-6 py-4"
        actions={
          <Button icon="plus" onClick={() => setShowForm({ initial: null })}>
            Yeni Sabit
          </Button>
        }
      />

      <Card padded={false}>
        <SistemSabitleriInfoBox />
        <Table
          columns={columns}
          rows={items}
          loading={status === 'loading'}
          emptyMessage={
            status === 'failed'
              ? 'Sistem sabitleri alınamadı. Sunucuya ulaşılamıyor — servis yayına alındıktan sonra yeniden deneyin.'
              : 'Henüz sistem sabiti tanımlanmamış'
          }
        />
        <div className="px-4 pb-4 pt-2">
          <Pagination
            page={page}
            size={size}
            total={pagination?.total ?? 0}
            onPageChange={setPage}
            onSizeChange={(newSize) => {
              setSize(newSize);
              setPage(1);
            }}
            loading={status === 'loading'}
          />
        </div>
      </Card>

      <ParamForm
        open={!!showForm}
        onClose={() => setShowForm(null)}
        onSaved={() => dispatch(fetchParameters({ page, size }))}
        initial={showForm?.initial}
      />

      <DeleteModal
        open={!!deleteTarget}
        title="Sistem sabitini silmek istediğinize emin misiniz?"
        description={
          deleteTarget
            ? `"${deleteTarget.param_key}" sabiti kalıcı olarak silinecek. Bu işlem geri alınamaz.`
            : 'Bu işlem geri alınamaz.'
        }
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return;
          const key = deleteTarget.param_key;
          const res = await dispatch(deleteParameter(deleteTarget.id));
          if (res.meta.requestStatus === 'fulfilled') {
            dispatch(
              pushToast({
                kind: 'success',
                message: `"${key}" sabiti silindi`,
              }),
            );
          }
        }}
      />

      {/* Kullanim > 0 icin bilgilendirme — silme yapilamaz. DeleteModal'in
          aksine "Sil" butonu yok; sadece "Tamam" ile kapanir. accentColor sari
          ve confirmIcon='refresh' ile silme cagrismasi onlenir. */}
      <DeleteModal
        open={!!blockedDeleteTarget}
        title="Bu sabit silinemez"
        description={
          blockedDeleteTarget
            ? `"${blockedDeleteTarget.param_key}" sabiti şu anda ${blockedDeleteTarget.usage_count} görevde kullanılıyor. Silmek için önce bu sabiti kullanan görevleri güncelleyin veya kaldırın.`
            : ''
        }
        onClose={() => setBlockedDeleteTarget(null)}
        onConfirm={() => setBlockedDeleteTarget(null)}
        deleteLabel="Tamam"
        cancelLabel="Kapat"
        accentColor="#F79009"
        confirmIcon="refresh"
      />
    </div>
  );
}
